import APIV5 from '~/models/api';

/* globals Knitiv */

export default class CollaboraOnlineController {
	/* @ngInject */
	/**
	 * @param {import('angular').IRootElementService} $element
	 * @param {import('angular').IHttpService} $http
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').ISCEService} $sce
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').IWindowService} $window
	 * @param {any} Notification
	 */
	constructor($element, $http, $rootScope, $sce, $scope, $timeout, $translate, $window, Notification) {
		this.$element = $element;
		this.$http = $http;
		this.$rootScope = $rootScope;
		this.$sce = $sce;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$window = $window;
		this.Notification = Notification;

		/** @type {Function | undefined} */
		this.change = undefined;

		/** @type {boolean} */
		this.disabled = false;

		/** @type {import('~/scripts/services/model/file-upload').default | undefined} */
		this.file = undefined;

		/** @type {?import('~/scripts/services/model/form').default} */
		this.form = null;

		/** @type {import('~/components/kform/kform.controller').default | undefined} */
		this.kformCtrl = undefined;

		this.locale = this.$rootScope.context?.lang.id ?? 'en_GB';

		/** @type {import('~/scripts/services/model/node').default | undefined} */
		this.node = undefined;

		/** @type {string | undefined } */
		this.collaboraOnlineServer = undefined;

		this.collaboraOnlineSource = '';
		this.cssCustomization = '';
		this.cssVariablesPreview = '';
		this.uiMode = '';
		this.uiTheme = '';
		this.savedUIState = false;
		this.showSidebar = true;
		this.hideToolbar = false;
		this.onScreenKeyboardHint = false;
		this.uiDefaultsPreview = '';
		this.receiveHandleMessage = undefined;
		this.wopiSrc = '';

		// TODO: Word editor by default?
		this.mimetype = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

		this.locationOrigin = window.location.origin;
		this.promiseFileInfo = undefined;
		this.scheme = this.locationOrigin.startsWith('https') ? 'https' : 'http';
	}

	buildCollaboraSrc() {
		if (!this.wopiSrc) {
			return;
		}
		const url = new URL(this.wopiSrc);
		if (this.node?.kid) {
			// @ts-ignore
			const path = this.$rootScope.context?.config.FILE?.url;
			const WOPISource = `${path}/api/v2/collabora/wopi/files/${this.file?.id ?? this.node.kid}`
			url.searchParams.set('WOPISrc', WOPISource);
		}

		if (this.disabled) {
			url.searchParams.set('permission', 'readonly');
		}
		if (this.locale) {
			url.searchParams.set('lang', this.locale);
		}
		if (this.$rootScope.context?.token.id) {
			url.searchParams.set('access_token', this.$rootScope.context?.token.id);
		}

		this.$timeout(() => {
			this.collaboraOnlineSource = this.$sce.trustAsResourceUrl(url.toString());
			this.receiveHandleMessage = (/** @type {MessageEvent<any>} */ event) => this.receiveMessage(event);
			this.$window.addEventListener('message', this.receiveHandleMessage, false)
		});
	}

	/**
	 * @returns {Promise<*>}
	 */
	async getDiscovery() {
		try {
			if (!this.collaboraOnlineServer) {
				throw new Error('missing config COLLABORA.url');
			}
			const response = await this.$http.get(`${this.collaboraOnlineServer}/hosting/discovery`);
			return response.data;
		}
		catch (error) {
			console.error('response', error);
		}
	}

	/**
	 *
	 * @param {Document} discoveryParsed
	 * @param {string} mimetype
	 * @returns {?string}
	 */
	getWopiSrcUrl(discoveryParsed, mimetype) {
		if (!(discoveryParsed instanceof Document)) { // Vérifie si c'est bien un objet Document
			console.error('Le paramètre \'discoveryParsed\' doit être un objet Document XML.');
			return null;
		}

		// XPath en JavaScript est différent de PHP. On utilise selectNodes ou evaluate pour parcourir le DOM.
		// Pour des cas simples, querySelectorAll est souvent suffisant.
		// L'équivalent de XPath '/wopi-discovery/net-zone/app[@name=\'%s\']/action'
		// pourrait être réalisé en sélectionnant les éléments et en filtrant.

		// const s = `wopi-discovery > net-zone > app`
		// const apps = discoveryParsed.querySelectorAll(s);
		// const names = [...apps].map(app => `'${app.getAttribute('name')?.toString()}': 'office',`)
		// console.log(names.sort().join('\n'));

		// Utilisation de querySelectorAll pour trouver les actions d'application correspondant au mimetype
		const selector = `wopi-discovery > net-zone > app[name="${mimetype}"] > action`;
		const actions = discoveryParsed.querySelectorAll(selector);
		if (actions && actions.length > 0) {
			// Retourne l'attribut 'urlsrc' du premier élément trouvé
			const attribute = actions[0].getAttribute('urlsrc')
			if (attribute) {
				const urlsrc = attribute.toString();
				// fix https
				if (this.collaboraOnlineServer?.startsWith('https:') && urlsrc.startsWith('http')) {
					return urlsrc.replace('http:', 'https:');
				}
				return urlsrc;
			}
		}

		return null;
	}

	/**
	 * Initialise le client WOPI en récupérant l'URL WopiSrc à partir d'un serveur Collabora Online.
	 * Cette fonction émule la logique de la fonction PHP 'main' en utilisant les paramètres d'URL.
	 *
	 * @returns {Promise<{success: boolean, message?: string, wopiSrc?: string}>}
	 * Un objet contenant le statut de succès, un message et l'URL WopiSrc si réussie.
	 */
	async initializeWopiClient() {
		if (!this.collaboraOnlineServer) {
			return {
				success: false,
				message: 'URL du serveur Collabora Online manquante.',
			}; // Équivalent de 201
		}


		// strStartsWith en JS est String.prototype.startsWith
		if (!this.collaboraOnlineServer.startsWith('http')) {
			return {
				success: false,
				message: 'L\'URL du serveur Collabora Online doit commencer par \'http\' ou \'https\'.',
			}; // Équivalent de 204
		}

		// Comparaison avec le schéma de l'hôte
		// Note: window.location.protocol inclut le ':' final, donc l'ajouter à hostScheme.
		const hostScheme = window.location.protocol; // Ex: "https:" ou "http:"
		if (!this.collaboraOnlineServer.startsWith(hostScheme + '//')) {
			return {
				success: false,
				message: 'Le schéma de l\'URL du serveur Collabora Online ne correspond pas au schéma de l\'hôte.',
			}; // Équivalent de 202
		}

		const discoveryXmlString = await this.getDiscovery();
		if (!discoveryXmlString) {
			return {
				success: false,
				message: 'Impossible de récupérer le document de découverte WOPI.',
			}; // Équivalent de 203
		}

		const parser = new DOMParser();
		const discoveryParsed = parser.parseFromString(discoveryXmlString, 'application/xml');

		// Vérifiez les erreurs de parsing XML
		if (discoveryParsed.querySelectorAll('parsererror').length > 0) {
			// En cas d'erreur de parsing XML, récupérer le contenu de l'erreur
			const parserError = discoveryParsed.querySelectorAll('parsererror')[0].textContent;
			console.error('Erreur lors de l\'analyse du document XML de découverte:', parserError);
			return {
				success: false,
				message: `Erreur lors de l'analyse du document de découverte WOPI: ${parserError}`,
			}; // Équivalent de 102
		}

		const wopiSource = this.getWopiSrcUrl(
			discoveryParsed,
			this.mimetype,
		);
		if (!wopiSource) {
			return {
				success: false,
				message: `URL WopiSrc pour <samp>${this.mimetype}</samp> non trouvée dans le document de découverte.`,
			}; // Équivalent de 103
		}

		// Enregistrez l'URL WopiSrc si nécessaire, ou renvoyez-la.
		// Dans ce contexte JavaScript client-side, vous pourriez vouloir l'utiliser directement.
		// L'équivalent de $_WOPI_SRC est maintenant la valeur retournée.
		return {
			success: true,
			wopiSrc: wopiSource,
		}; // Équivalent de 0
	}

	/**
	 * @param {any} message
	 */
	postMessage(message) {
		// @ts-ignore
		const { contentWindow } = this.$element.find('.collabora-online-iframe').get(0);
		if (contentWindow) {
			contentWindow.postMessage(JSON.stringify(message), '*');
		}
		else {
			console.error('collabora contentWindow not found!');
		}
	}

	/**
	 * @param {MessageEvent<any>} event
	 */
	async receiveMessage(event) {
		/** @type {any} */
		let message;
		try {
			message = JSON.parse(event.data);
		}
		catch {
			message = event.data;
		}
		if (!message) {
			return;
		}

		// eslint-disable-next-line no-console
		console.log('==== receiveMessage', message);
		if (
			message.MessageId === 'App_LoadingStatus'
			&& message.Values?.Status === 'Document_Loaded'
		) {
			this.postMessage({'MessageId': 'Host_PostmessageReady'});
		}

		if (
			this.file
			&& message.MessageId === 'Doc_ModifiedStatus'
			&& message.Values?.Modified === true
		) {
			const nodeid = this.file.id ?? this.node?.kid;
			if (nodeid) {
				if (this.promiseFileInfo) {
					this.$timeout.cancel(this.promiseFileInfo);
				}
				this.promiseFileInfo = this.$timeout(async () => {
					const info = await APIV5.instance.fileInfo({ nodeid }, { id: 'collabora-online.receive-message' });
					if (this.file) {
						this.file.file.size = info.size;
						if (this.change) {
							this.change(new Event('change'));
						}
					}
				}, 1000);
			}
		}

		if (
			message.MessageId === 'Action_Save_Resp'
			&& message.Values?.success === true
			&& this.kformCtrl?.loading === false
			&& !this.form?.readOnly
		) {
			this.$rootScope.$broadcast('Form.save');
		}
	}

	/** @param {import('angular').IOnChangesObject} changes */
	$onChanges(changes) {
		if (
			(changes.disabled && changes.disabled.previousValue !== changes.disabled.currentValue)
			|| (changes.file && changes.file.previousValue !== changes.file.currentValue)
		) {
			this.buildCollaboraSrc();
		}
	}

	$onDestroy() {
		if (this.receiveHandleMessage) {
			this.$window.removeEventListener('message', this.receiveHandleMessage)
		}
	}

	async $onInit() {
		this.collaboraOnlineServer = this.$rootScope.context?.config?.COLLABORA?.url;
		if (this.file) {
			this.mimetype = this.file.file.type;
		}

		const returnValue = await this.initializeWopiClient();
		if (returnValue.success && returnValue.wopiSrc) {
			this.wopiSrc = returnValue.wopiSrc;
			this.buildCollaboraSrc();
			this.$scope.$on('changeLanguage', (/** @type {Event} */ _$event, /** @type {any} */ lang) => {
				this.locale = lang.id;
				this.buildCollaboraSrc();
			});

			this.$scope.$on('collabora.save', () => {
				this.postMessage({
					MessageId: 'Action_Save',
					Values: {
						Notify: true,
					},
				});
			});
		}
		else {
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message: returnValue.message,
			});
		}
	}
}
